import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Export, PlusSquare, DotsThreeVertical, DownloadSimple, LinkSimple, DeviceMobile } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { browserKind, isMobile } from "@/lib/installPrompt";

const SECTIONS = [
  {
    id: "iphone",
    title: "iPhone & iPad (Safari)",
    match: (k) => k === "ios",
    steps: [
      <>Open <b>selfagency.ca</b> in <b>Safari</b> (it must be Safari, not Chrome).</>,
      <>Tap the <Export size={14} className="inline -mt-0.5" color="#FFE01A" weight="bold" /> <b>Share</b> button at the bottom of the screen.</>,
      <>Scroll down and tap <PlusSquare size={14} className="inline -mt-0.5" color="#FFE01A" weight="bold" /> <b>Add to Home Screen</b>, then <b>Add</b>.</>,
    ],
    result: "The Self-Agency icon appears on your home screen like a normal app — full screen, no browser bars.",
  },
  {
    id: "android-chrome",
    title: "Android phone (Chrome)",
    match: (k, mobile) => k === "chrome" && mobile,
    steps: [
      <>Open <b>selfagency.ca</b> in <b>Chrome</b>.</>,
      <>Tap the <DotsThreeVertical size={14} className="inline -mt-0.5" color="#FFE01A" weight="bold" /> menu (top right).</>,
      <>Tap <b>Add to Home screen</b> (or <b>Install app</b>), then <b>Install</b>.</>,
    ],
    result: "The icon lands on your home screen and in your app drawer, just like an app from the Play Store.",
  },
  {
    id: "android-samsung",
    title: "Android phone (Samsung Internet)",
    match: (k) => k === "samsung",
    steps: [
      <>Open <b>selfagency.ca</b> in Samsung Internet.</>,
      <>Tap the <b>menu (≡)</b> at the bottom right.</>,
      <>Tap <b>Add page to</b> → <b>Home screen</b>. (A download icon in the address bar installs it too.)</>,
    ],
    result: "The icon appears on your home screen.",
  },
  {
    id: "windows-chrome",
    title: "Windows & Mac (Chrome)",
    match: (k, mobile) => k === "chrome" && !mobile,
    steps: [
      <>Open <b>selfagency.ca</b> in <b>Chrome</b>.</>,
      <>Click the small <b>install icon</b> at the right end of the address bar — or menu <DotsThreeVertical size={14} className="inline -mt-0.5" color="#FFE01A" weight="bold" /> → <b>Cast, save and share</b> → <b>Install page as app</b>.</>,
      <>Click <b>Install</b>. Leave "Create desktop shortcut" checked if asked.</>,
    ],
    result: "The app opens in its own window with its own icon — in the Start Menu/taskbar on Windows, or Launchpad/Dock on Mac. No browser needed from then on.",
    note: <>Seeing <b>"Open in The Self-Agency Project"</b> in that menu instead of "Install page as app"? The app is <b>already installed</b> on this computer — click it to open the app, then right-click its taskbar icon → <b>Pin to taskbar</b>, or find "Self-Agency" in the Start Menu and drag it to your desktop.</>,
  },
  {
    id: "windows-edge",
    title: "Windows (Microsoft Edge)",
    match: (k, mobile) => k === "edge" && !mobile,
    steps: [
      <>Open <b>selfagency.ca</b> in <b>Edge</b>.</>,
      <>Click the install icon at the right end of the address bar — or menu <b>(…)</b> → <b>Apps</b> → <b>Install this site as an app</b>.</>,
      <>Click <b>Install</b>.</>,
    ],
    result: "The app gets its own window and icon; Edge offers to pin it to the taskbar and Start Menu.",
  },
  {
    id: "firefox",
    title: "Firefox",
    match: (k) => k === "firefox",
    steps: [
      <>On a <b>phone</b>: tap the menu, then <b>Add to Home screen</b>.</>,
      <>On a <b>computer</b>: desktop Firefox can't install web apps — open selfagency.ca in <b>Chrome or Edge</b> once and install from there.</>,
    ],
    result: "On phones you get a home-screen icon; on computers use Chrome/Edge for the full app experience.",
  },
];

export default function InstallGuidePage() {
  const [copied, setCopied] = useState(false);
  const [mine] = useState(() => {
    const k = browserKind(); const m = isMobile();
    const hit = SECTIONS.find((s) => s.match(k, m));
    return hit ? hit.id : null;
  });
  useEffect(() => { document.title = "Install the app — The Self-Agency Project"; }, []);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText("https://selfagency.ca/install-guide"); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };

  const ordered = mine ? [...SECTIONS].sort((a, b) => (a.id === mine ? -1 : b.id === mine ? 1 : 0)) : SECTIONS;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-5 py-14" data-testid="install-guide-page">
        <Link to="/" className="text-[#FFE01A] text-sm font-bold uppercase tracking-wide no-underline">&larr; selfagency.ca</Link>
        <h1 className="text-4xl sm:text-5xl font-extrabold font-display mt-4 mb-1" style={{ color: "#fff" }}>Put Self-Agency on your device</h1>
        <p className="text-[#A1A1AA] text-sm mb-6">Two minutes, no app store. The course gets its own icon on your home screen or desktop and opens full screen — no browser bars.</p>

        <button onClick={copyLink} data-testid="install-guide-copy-link"
          className="inline-flex items-center gap-2 bg-[#FFE01A] text-black font-bold text-sm px-4 py-2 rounded-full border-0 cursor-pointer hover:opacity-90">
          <LinkSimple size={16} weight="bold" /> {copied ? "Link copied!" : "Copy link to share this guide"}
        </button>

        {!isMobile() && (
          <div className="mt-6 rounded-xl border border-[#FFE01A]/40 p-4 flex items-center gap-4" data-testid="install-guide-qr">
            <div className="bg-white p-2 rounded-lg shrink-0">
              <QRCodeSVG value={`${window.location.origin}/install-guide`} size={110} />
            </div>
            <div>
              <div className="font-bold font-display text-white">Want it on your phone instead?</div>
              <p className="text-[#A1A1AA] text-sm m-0 mt-1">Point your phone's camera at this code — it opens this guide on your phone with the right steps for your device already highlighted.</p>
            </div>
          </div>
        )}

        <div className="mt-8 space-y-4">
          {ordered.map((s) => (
            <div key={s.id} id={s.id} data-testid={`install-section-${s.id}`}
              className="rounded-xl p-4 border"
              style={s.id === mine ? { borderColor: "#FFE01A", background: "rgba(255,224,26,0.06)" } : { borderColor: "rgba(255,255,255,0.1)" }}>
              <div className="flex items-center gap-2 mb-2">
                <DeviceMobile size={16} color="#FFE01A" weight="duotone" />
                <h2 className="text-base font-bold font-display m-0" style={{ color: "#fff" }}>{s.title}</h2>
                {s.id === mine && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: "#FFE01A", color: "#000" }}>Your device</span>}
              </div>
              <ol className="text-[#D4D4D8] text-sm leading-relaxed pl-5 m-0 space-y-1">
                {s.steps.map((st, i) => <li key={i}>{st}</li>)}
              </ol>
              <p className="text-[#A1A1AA] text-xs mt-2 mb-0"><b className="text-[#D4D4D8]">Result:</b> {s.result}</p>
              {s.note && <p className="text-xs mt-2 mb-0 rounded-lg px-3 py-2" style={{ background: "rgba(255,224,26,0.08)", color: "#D4D4D8" }}>{s.note}</p>}
            </div>
          ))}
        </div>

        <h2 className="text-lg font-bold font-display mt-10 mb-2" style={{ color: "#fff" }}>Installed it, but no desktop icon?</h2>
        <ul className="text-[#D4D4D8] text-sm leading-relaxed pl-5 space-y-1 m-0">
          <li><b>Windows:</b> open the Start Menu, search <b>Self-Agency</b>, right-click it → <b>Pin to taskbar</b>, or drag it onto the desktop.</li>
          <li><b>Mac:</b> find <b>Self-Agency</b> in Launchpad and drag it to the Dock.</li>
          <li><b>Phones:</b> check the app drawer — you can drag the icon anywhere on your home screen.</li>
        </ul>

        <p className="text-[#71717A] text-xs mt-10 flex items-center gap-1.5">
          <DownloadSimple size={13} /> Teachers & admins: share this page with anyone — selfagency.ca/install-guide
        </p>
      </div>
    </div>
  );
}
